<script setup>
import axios from "axios";
import {computed, nextTick, onMounted, reactive, ref, toRaw, watch} from 'vue';
import {useAuthStore} from "../../stores/auth.js";
import {useSocketStore} from "../../stores/socketStore.js";
import useSocketListeners from "../../composables/useSocketListeners.js";
import {useSessionStore} from '../../stores/sessionStore';
import {chatNoticeFrom} from '../../helpers/chatNotice';
import GroupsComponent from "./GroupsComponent.vue";
import {saveInteractionData} from "../../helpers/interactionHelper";
import IconAttendees from "../icons/IconAttendees.vue";
import IconAttendeesSelected from "../icons/IconAttendeesSelected.vue";
import IconBarChartHorizontal from "../icons/IconBarChartHorizontal.vue";
import IconQandA from "../icons/IconQandA.vue";
import IconFiles from "../icons/IconFiles.vue";
import IconChats from "../icons/IconChats.vue";
import IconChatsSelected from "../icons/IconChatsSelected.vue";
import IconClose from "../icons/IconClose.vue";
import IconLogo from "../icons/IconLogo.vue";
import Loading from "./shared/loading/Loading.vue";
import Modal from "./shared/modals/ModalComponent.vue";
import NotificationBubble from "./shared/notifications/NotificationBubble.vue";
import NotificationsCount from "./shared/notifications/NotificationsCount.vue";
import NotificationToast from "./shared/notifications/NotificationToast.vue";
import UsersComponent from "./UsersComponent.vue";
import QnAWidget from "../session-widgets/QnAWidget.vue";
import PollsWidget from "../session-widgets/PollsWidget.vue";
import BreakoutRoomsWidget from "../session-widgets/BreakoutRoomsWidget.vue";
import EventResources from "./EventResources.vue";
import SessionPanelIcon from "./SessionPanelIcon.vue";
import { chatInScope, bubblesToShow } from "../../helpers/sessionPanelScope";

const props = defineProps({
  hideHeader: {
    type: Boolean,
    default: false,
  },
  items: {
    type: Array,
    default: () => [],
  },
  inSession: {
    type: Boolean,
    default: false,
  },
  session: {
    type: Object,
    default: null,
  },
  inSessionUsers: {
    type: Array,
    default: () => [],
  },
  event: {
    type: Object,
    default: null,
  },
  userRole: {
    type: String,
    default: "attendee",
  },
  isModerator: {
    type: Boolean,
    default: false,
  },
});

// Reactive state
const template = ref('default');
const showOnlyNotification = ref(false);
// A session's panel opens on People, for every role and session type
// (In-session panel: People by default, only this session's people and chat).
const view = ref('attendees');
const videoUrl = ref(null);
const jitsi = reactive({
  start: false,
  api: null,
  inVideo: false,
  warning: false,
  group: null,
  isFull: false,
});
const isLoading = ref(true);
const userView = ref(null);
const user = ref(null);
const newUser = ref({});
const newUserReady = ref(false);
const userSearch = ref('');
const groupSearch = ref('');
const paramGroup = ref(null);
const selectedGroup = ref(null);
const selectedArea = ref(null);
const areas = ref([]);
const groups = ref([]);
const allUsers = ref([]);
const users = ref([]);
// Hub-level peer-to-peer messaging toggle, surfaced by the websocket in the
// area_refresh payload. Defaults enabled; server-side enforcement is authoritative.
const peerMessagingEnabled = ref(true);
const notifications = ref([]);
const qnaNotifications = ref(0);
const pollsNotifications = ref(0);
const isChatHidden = ref(false);
const notificationToast = reactive({visible: false});
const jitsiLoaded = ref(false);
const authStore = useAuthStore();
const socketStore = useSocketStore();
const socketListeners = useSocketListeners();
const sessionStore = useSessionStore();
const store = window.store;
const groupsComponentRef = ref(null);

// Computed properties
const userList = computed(() => {
  let filteredUsers = [...users.value];

  const onlineUsers = [...filteredUsers]
      .filter((u) => u.online && !u.invisible)
      .sort((a, b) => a.fullName.localeCompare(b.fullName));

  const offlineUsers = [...filteredUsers]
      .filter((u) => !u.online && !u.invisible)
      .sort((a, b) => a.fullName.localeCompare(b.fullName));

  const allFilteredUsers = [...onlineUsers, ...offlineUsers];

  if (userSearch.value) {
    return allFilteredUsers.filter((u) => {
      return u.fullName
          .toLowerCase()
          .includes(userSearch.value.toLowerCase());
    });
  }

  return allFilteredUsers;
});

const sessionId = computed(() => {
  const s = props.session || props.event;
  if (!s) return "";
  if (sessionStore.isInBreakout && sessionStore.breakoutRoomName) {
    const domain = window.store?.subdomain || (props.event && props.event.domain) || "";
    const startTimePart = sessionStore.breakoutStartTime ? `_${sessionStore.breakoutStartTime}` : '';
    return `${domain}_${s.id}_${sessionStore.breakoutRoomName}${startTimePart}`;
  }
  return String(s.id);
});

watch(sessionId, (newId) => {
  // Into or out of a breakout: the room's own chat is a different one, and the
  // server answers only for the room asked about.
  if (props.inSession) getGroups();
  if (selectedGroup.value && selectedGroup.value.isSessionGroup) {
    const s = props.session || props.event;
    const mainSessionId = s ? String(s.id) : null;
    const currentGroupId = String(selectedGroup.value.sessionId);

    // If the selected group is neither the current room nor the main session, close it
    if (currentGroupId !== String(newId) && currentGroupId !== mainSessionId) {
      selectedGroup.value = null;
      view.value = 'groups';
    }
  }
});

// Hub Staff / Hub Owner. Mirrors the websocket's HUB_STAFF_ROLES.
const HUB_STAFF_ROLES = ['owner', 'event-planner', 'admin', 'administrator', 'super-admin'];
const isStaffOrOwnerRole = (role) => HUB_STAFF_ROLES.includes(String(role || '').toLowerCase());

// Group participants carry no role (mongo users lack it) — resolve via the
// role-enriched allUsers list from area_refresh.
const getParticipantRole = (userId) => {
  const u = allUsers.value.find((au) => Number(au.user_id) === Number(userId));
  return u ? u.role : null;
};

// Is the signed-in user Hub Staff / the Hub Owner? Prefers the role-enriched
// allUsers list (SSO-sourced, refreshed by area_refresh) and falls back to the
// auth store while that is still loading. Client-side gating only — the
// websocket re-resolves the role from the handshake-verified id before it lets
// anything through.
const currentUserRole = computed(
  () => getParticipantRole(authStore.userData?.id) || authStore.userRole
);

const currentUserIsHubStaff = computed(() => isStaffOrOwnerRole(currentUserRole.value));

// When hub peer-to-peer messaging is off, a Member keeps ONLY the chats whose
// every other participant is Hub Staff / the Hub Owner — the same chats they
// would still be allowed to start. Everything else is hidden: member↔member
// one-to-ones, member group chats (any size), and session chats, which are
// member↔member groups. Nothing is deleted; all of it returns when the toggle
// does. Hub Staff / the Hub Owner are exempt and keep every chat.
//
// The websocket is authoritative and applies the identical rule — it filters
// these out of /groups and /notifications and rejects select/send/react
// (createGroupAccessFilter). This mirror exists so the list reacts to a toggle
// without waiting on a re-fetch, and therefore fails OPEN when a participant's
// role can't be resolved from `allUsers`: the fetched list is already filtered,
// so a local mis-resolve must never hide a chat the server permits.
const isHiddenPeerChat = (g) => {
  if (peerMessagingEnabled.value) return false;
  if (!g) return false;
  if (currentUserIsHubStaff.value) return false;
  if (g.isSessionGroup) return true;

  const myId = Number(authStore.userData?.id);
  const otherIds = [...new Set(
    [...(g.users || []), ...(g.pendingUsers || [])]
      .map((u) => (u && typeof u === 'object' ? u.user_id : u))
      .filter((id) => id !== undefined && id !== null)
      .map(Number)
  )].filter((id) => id !== myId);

  if (!otherIds.length) return false;
  // Role unknown (user not in the area payload yet) — defer to the server.
  if (otherIds.some((id) => getParticipantRole(id) == null)) return false;

  return !otherIds.every((id) => isStaffOrOwnerRole(getParticipantRole(id)));
};

// Whether a chat belongs where this panel is: in a session, only that room's
// own chat; outside one, any chat but a session's.
const inScope = (g) => chatInScope(g, { inSession: props.inSession, sessionId: sessionId.value });

const groupList = computed(() => {
  const s = props.session || props.event;
  const mainSessionId = s ? String(s.id) : null;

  const filteredGroups = groups.value.filter((g) => {
    // Hide member↔member chats while peer messaging is disabled.
    if (isHiddenPeerChat(g)) return false;

    // "Enable In-Session Chat" off, or hub peer-to-peer messaging off: drop the
    // session chats, keeping every other chat in the list. Only applies
    // in-session — sessionChatsVisible is always true outside one.
    if (g.isSessionGroup && !sessionChatsVisible.value) return false;

    // Inside a session there is one conversation: this room's own chat.
    // Outside one, no session chat is listed — it ends with its session and is
    // kept against it, not carried into the hub (the server scopes the same
    // way; this also covers a session chat announced over the socket).
    if (props.inSession) return g.isSessionGroup && String(g.sessionId) === String(sessionId.value);
    return !g.isSessionGroup;
  });

  if (groupSearch.value) {
    return filteredGroups.filter((g) => {
      return g
          ? g.name.toLowerCase().includes(groupSearch.value.toLowerCase())
          : false;
    });
  }

  return filteredGroups;
});

const notificationsCount = computed(() => {
  return toRaw(groups.value)
      .filter((g) => {
        // Don't count notifications for chats hidden by the peer-messaging gate.
        if (isHiddenPeerChat(g)) return false;

        // ...nor for session chats hidden by the per-session or hub toggle.
        if (g.isSessionGroup && !sessionChatsVisible.value) return false;

        // The same scope as the list: in a session, its own chat's unread
        // only; outside one, never a session chat's.
        if (!inScope(g)) return false;

        g.notifications = notifications.value.filter(
            (n) => n.groupId === g._id
        ).length;

        return g;
      })
      .reduce((prevValue, r) => r.notifications + prevValue, 0);
});

// Notification bubbles are a second way into a chat — they render the message
// and offer a reply box. A chat hidden by either gate must not surface one,
// otherwise a Member could read and answer a session chat that the In Session
// tab no longer shows them. Notifications without a resolvable group (video
// invites) always pass through.
// Inside a session there are none at all (helpers/sessionPanelScope).
const visibleNotifications = computed(() => bubblesToShow(notifications.value, {
  inSession: props.inSession,
  hiddenGroupIds: groups.value
    .filter((g) => isHiddenPeerChat(g) || (g.isSessionGroup && !sessionChatsVisible.value) || !inScope(g))
    .map((g) => String(g._id)),
}));

// The server filters the fetched list by this same rule, so BOTH directions need
// a re-fetch: turning it off drops the now-hidden chats, turning it back on is
// the only thing that brings them back. And a member↔member chat left open when
// it flips off must close, so it isn't still readable/usable after vanishing
// from the list.
watch(peerMessagingEnabled, (enabled) => {
  refreshGroups();

  if (!enabled && selectedGroup.value && isHiddenPeerChat(selectedGroup.value)) {
    selectedGroup.value = null;
    if (view.value === 'messages') view.value = props.inSession ? 'attendees' : 'groups';
  }
});

const participantIdentity = computed(() => {
  return String(authStore.userData.id);
});

const userName = computed(() => {
  return authStore.userData?.fullName || authStore.userData?.name || "Guest";
});

// The session's own "Enable In-Session Chat" toggle — and ONLY that. When off,
// the SESSION chat is hidden and messaging into it is blocked for everyone
// (members, staff, owner).
//
// Deliberately NOT combined here with the hub's peer-to-peer setting. They
// restrict different things: peer-to-peer blocks member↔MEMBER chats while
// leaving Hub Staff / Hub Owner reachable, whereas this toggle governs the
// session chat for everybody. ANDing them made disabling peer-to-peer
// blanket-block all chat creation inside a session, so a member could message
// Hub Staff from the hub but not once they joined a session. The per-target
// peer-to-peer rule lives in UsersComponent (isPeerMessagingBlocked /
// isUserRestricted), which correctly exempts Staff/Owner, and is enforced
// server-side by isChatCreationAllowed.
//
// The two ARE combined for one narrow purpose — whether the "In Session" chats
// surface is shown at all — in `sessionChatsVisible` below. Use that for
// visibility, this one for send-blocking.
//
// Scope: this hides the "In Session" chats only. The Chats tab itself stays —
// one-to-one and hub-wide group chats are not governed by a per-session toggle,
// and removing the whole tab would cut members off from them mid-session.
// Hub-wide chat (outside a session) is unaffected entirely.
// Server-authoritative value, fetched below. The session object reaches this
// component from several surfaces backed by DIFFERENT API resources, and one
// that omits `in_session_chat_enabled` makes the payload read `undefined` —
// which is not `false`, so the toggle silently appears to do nothing. Never
// trust the payload alone; it is only the fallback until the fetch lands.
const sessionChatEnabledFromApi = ref(null);

const agendaEventIdForChat = computed(() => (props.session || props.event)?.id ?? null);

/**
 * What this session's host said the room may do: chat, Q&A, polls, breakout
 * rooms (Session settings are not applied in the room → "a section appears in
 * the session panel only when its setting is on for that session").
 *
 * Q&A, polls and breakouts live in the session store, so the panel, its folded
 * rail (EventChat) and the dock read one answer. They are seeded from the
 * occurrence the room was opened from (its own `session_content_settings`),
 * so nothing switched off is drawn — not even for the moment before the
 * content-settings lookup lands — and then confirmed by that lookup, and by
 * every change a host makes while the room runs (session-content:settings).
 *
 * An absent flag reads as on: a session created before the columns existed,
 * or a lookup that fails, must not silently lose a feature it has always had —
 * the same reasoning the resource uses when it reads an absent column as true.
 */
const roomFeatures = reactive({
  get qa() { return sessionStore.qaEnabled; },
  get polls() { return sessionStore.pollsEnabled; },
});

const applyRoomFeatures = (settings = {}) => {
  if (typeof settings.qa_enabled === 'boolean') sessionStore.qaEnabled = settings.qa_enabled;
  if (typeof settings.polls_enabled === 'boolean') sessionStore.pollsEnabled = settings.polls_enabled;
  if (typeof settings.breakouts_enabled === 'boolean') sessionStore.breakoutsEnabled = settings.breakouts_enabled;
  if (typeof settings.files_enabled === 'boolean') sessionStore.filesEnabled = settings.files_enabled;
};

// The occurrence's own settings, as the agenda sent them with it.
const seedRoomFeatures = () => {
  const s = props.session || props.event;
  if (!props.inSession || !s) return;
  applyRoomFeatures({ ...(s.session_content_settings ?? {}), ...pickFlags(s) });
};
const pickFlags = (s) => Object.fromEntries(['qa_enabled', 'polls_enabled', 'breakouts_enabled', 'files_enabled']
  .filter((k) => typeof s?.[k] === 'boolean').map((k) => [k, s[k]]));

const fetchSessionChatSetting = async () => {
  if (!props.inSession || !agendaEventIdForChat.value) return;
  try {
    const { data } = await axios.get(
      `/api/agenda-events/${agendaEventIdForChat.value}/content-settings`
    );
    if (typeof data?.in_session_chat_enabled === 'boolean') {
      sessionChatEnabledFromApi.value = data.in_session_chat_enabled;
    }

    applyRoomFeatures(data?.data ?? {});
  } catch (error) {
    // Keep the payload fallback — a failed lookup must not silence chat.
    console.error('Failed to resolve in-session chat setting:', error);
  }
};

// Re-resolve whenever the session changes (switching sessions reuses this
// component), and clear the stale answer so it can't leak across sessions.
watch(agendaEventIdForChat, () => {
  sessionChatEnabledFromApi.value = null;
  seedRoomFeatures();
  fetchSessionChatSetting();
}, { immediate: true });

// Office Hours is People only (XN-4999): nothing to type, share or poll at a desk.
const isOfficeHoursSession = computed(() => (props.session || props.event)?.video_call_type === 'office_hours');

const inSessionChatEnabled = computed(() => {
  if (!props.inSession) return true;
  // An Office Hours call has no chat, whatever its row says.
  if (isOfficeHoursSession.value) return false;
  const s = props.session || props.event;
  return sessionChatEnabledFromApi.value !== null
    ? sessionChatEnabledFromApi.value
    : (s ? (s.in_session_chat_enabled !== false) : true);
});

// Whether the "In Session" chats surface exists for THIS user: its sub-tab in
// Chats, the session chats in the list, their notification counts, and sending
// into them. Two independent gates, with deliberately different reach:
//
//   - The session's own "Enable In-Session Chat" toggle blocks EVERYONE —
//     Members, Hub Staff and the Hub Owner alike. It is a property of the
//     session, not of who is looking at it.
//   - The hub's peer-to-peer toggle blocks MEMBERS ONLY. A session chat is a
//     member↔member group, so it goes away for attendees, while Hub Staff and
//     the Hub Owner keep the tab and full send/receive — the same exemption
//     `isHiddenPeerChat` and UsersComponent's per-target rules apply, and the
//     same one the websocket enforces in canAccessGroup / canPostToSessionChat.
//
// Kept separate from `inSessionChatEnabled` so peer-to-peer never leaks into
// that flag: it also drives blanket send-blocking, and folding a role-dependent
// rule into it would block Staff too.
//
// Hub-wide chat (outside a session) is unaffected by THIS flag: `inSession` is
// false there, so `isHiddenPeerChat` is the only gate that applies — and it
// hides a Member's retained session chats too, matching what the server serves.
const sessionChatsVisible = computed(() => {
  if (!props.inSession) return true;
  if (!inSessionChatEnabled.value) return false;
  return peerMessagingEnabled.value || currentUserIsHubStaff.value;
});

// Chat turned off: the section goes for everybody in the session, so whoever
// was in it lands on People.
watch(sessionChatsVisible, (visible) => {
  if (!visible && selectedGroup.value?.isSessionGroup) selectedGroup.value = null;
  if (!visible && props.inSession && (view.value === 'messages' || view.value === 'groups')) view.value = 'attendees';
}, { immediate: true });

// The session's one conversation (In-session panel: "the only conversation
// inside a session is that session's own chat"). Opened as soon as the Chat
// section is: there is no list of chats in a session to choose from.
const sessionChat = computed(() => (props.inSession ? groupList.value[0] ?? null : null));
watch([view, sessionChat], ([v, chat]) => {
  if (!props.inSession || v !== 'groups' || !chat) return;
  selectGroup(chat);
});

// People's count on the rail: the people in this session, from the same list
// People shows, so the badge and the tab cannot disagree.
const peopleInSession = computed(() => sessionStore.sessionParticipants.filter((p) => !p.invisible).length);

const effectiveUserRole = computed(() => {
  // If explicitly passed and not 'attendee', use it
  if (props.userRole && props.userRole !== 'attendee') return props.userRole;

  // Try to derive from current user and session info
  const s = props.session || props.event;
  const user = authStore.userData;

  // The server's answer first. A seat is no longer proof that somebody may
  // speak — a policyholder asked to speak at a recorded session holds one
  // until they answer — so deriving it from `session_speakers` would treat a
  // pending invitee as a speaker. The derivation below stays for payloads that
  // predate the field.
  if (s?.viewer_role) return s.viewer_role;

  if (user && s) {
    const sessionModeratorId = Number(s.session_moderator?.id);
    const speakerIds = s.session_speakers?.map(sp => Number(sp.id)) || [];
    const currentUserId = Number(user.subdomain_user_id);

    if (currentUserId === sessionModeratorId) return 'admin';
    if (speakerIds.includes(currentUserId)) return 'speaker';
  }

  // Fallback to authStore role if available
  if (authStore.userRole) return authStore.userRole;

  return props.userRole; // Fallback to prop (default: 'attendee')
});

// Methods
const hideChat = (status) => {
  newUser.value.invisible = status;
};

const handleNotify = (message) => {
  notificationToast.visible = true;
  notificationToast.message = message;
  notificationToast.error = true;
};




const handleBack = () => {
  groupSearch.value = '';
  if (jitsi.start) {
    leaveVideo();
    template.value = "full";
  } else {
    view.value = 'groups';
  }
};

const handleAttendeeLeft = (groupId) => {
  groups.value = groups.value.filter((g) => g._id !== groupId);
};

const handleHideChat = () => {
  isChatHidden.value = !isChatHidden.value;
  if (!isChatHidden.value) {
    scrollToBottom();
  }
};

const setFullTemplate = () => {
  if (!jitsi.start && view.value !== "attendees") {
    view.value = "groups";

    // Auto-open from the DISPLAYED list, never the raw one — otherwise the
    // wide layout would open a chat the peer-messaging (or session) gate has
    // just hidden from the user.
    if (groupList.value.length > 0 && !jitsi.start) {
      template.value = "full";
    }

    if (selectedGroup.value) {
      selectGroup(selectedGroup.value);
    } else {
      if (groupList.value.length > 0) {
        selectGroup(groupList.value[0]);
      } else {
        template.value = "default";
      }
    }
  }
};

const setDefaultTemplate = () => {
  template.value = "default";
};

const handleChatsTransitionEnd = () => {
  if (selectedGroup.value && view.value !== "messages") {
    if (view.value === "groups") {
      handleChangeView("groups");
    } else {
      handleChangeView("attendees");
    }
  }
};


// Whether a section exists in this room. Its rail button, its folded-rail
// button (EventChat) and its pane all go by this, so a section switched off
// cannot be reached by a stale tab, a link, the keyboard or a click — and
// People always exists. Office Hours has People only.
const sectionAvailable = (section) => {
  if (!props.inSession) return true;
  if (isOfficeHoursSession.value) return section === 'attendees';
  switch (section) {
    case 'groups':
    case 'messages': return sessionChatsVisible.value;
    case 'qanda': return !sessionStore.isInBreakout && roomFeatures.qa;
    case 'polls': return !sessionStore.isInBreakout && roomFeatures.polls;
    case 'breakouts': return sessionStore.isAdmin && sessionStore.breakoutsEnabled;
    case 'files': return !sessionStore.isInBreakout && sessionStore.filesEnabled;
    default: return true;
  }
};

/**
 * The People list (UsersComponent) is laid over the whole panel
 * (`.users__content` is absolutely positioned), so it must be moved aside for
 * every section that is not its own — not only Chat. Left in place on Files it
 * covered the pane, empty, and took its clicks ("The Files tab is blank for
 * every role and Share a file can't be clicked"). In a session it is People's;
 * outside one it is also the chat list.
 */
const OTHER_SECTIONS = ['qanda', 'polls', 'files', 'breakouts'];
const peopleListHidden = computed(() => (selectedGroup.value && view.value === 'messages')
  || (props.inSession && view.value === 'groups')
  || OTHER_SECTIONS.includes(view.value));

const handleChangeView = (requested) => {
  // A section that does not exist here opens People instead (XN-5012).
  const newView = sectionAvailable(requested) ? requested : 'attendees';
  view.value = newView;
  jitsi.api = null;
  jitsi.isFull = false;
  selectedGroup.value = null;

  if (newView === "attendees" && template.value === "full") {
    template.value = "default";
  }

  if (newView === "groups" && document.getElementById("boxChat")) {
    const chatWidth = document.getElementById("boxChat").offsetWidth;

    if (chatWidth > 890) setFullTemplate();
  }

  sessionStore.setChatView(newView);
};

// A setting switched off while its section is open — or a section that was
// never on — leaves whoever is looking at it on People, never on a blank pane.
watch(() => props.inSession && !sectionAvailable(view.value), (gone) => {
  if (gone) handleChangeView('attendees');
});


const searchData = (type, value) => {
  if (type === "user") userSearch.value = value;
  if (type === "group") groupSearch.value = value;
};



const getGroupsNotifications = () => {
  socketStore
      .ensureToken()
      .then((hasToken) => {
        if (!hasToken || !socketStore.chatToken) return;

        return axios.get(
            `${socketStore.socketHost}/notifications/${window.store.subdomain}/${authStore.userData.id}`
            , {
              withCredentials: false,
              headers: { Authorization: `Bearer ${socketStore.chatToken}` },
            });
      })
      .then((response) => {
        if (response && response.data) fetchNotifications(response.data);
      })
      .catch((error) => {
        console.error('Error retrieving notifications:', error);
      });
};

// GroupsComponent reports a sent message; the group is ours, so the optimistic
// append happens here. Only when history is already loaded: otherwise the
// server's group_messages_refresh renders it, and `.push` on undefined would
// throw mid-send.
const onMessageSent = (messageText) => {
  const group = selectedGroup.value;
  if (group) {
    if (Array.isArray(group.messages)) {
      group.messages.push({
        user: {
          fullName: user.value.fullName,
          user_id: user.value.id,
        },
        message: messageText,
        created_at: new Date().toISOString()
      });
    }

    group.lastMessage = {
      full_name: user.value.fullName,
      message: messageText,
      created_at: new Date().toISOString()
    };
  }
  scrollToBottom();
};

const scrollToBottom = () => {
  nextTick(() => {
    if (selectedGroup.value) {
      const messagesContainer = document.querySelector(
          `#group-${selectedGroup.value._id} .messages__block__content__list`
      );
      if (messagesContainer)
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  });
};

const fetchNotifications = (notificationsData = null) => {
  if (notificationsData.length) {
    notifications.value = notificationsData.filter((n) => {
      return selectedGroup.value
          ? n.user === Number(authStore.userData.id) &&
          n.group !== selectedGroup.value._id &&
          !n.seen
          : n.user === Number(authStore.userData.id) && !n.seen;
    });
  } else {
    notifications.value = [];
  }

  groups.value = groups.value.map((group) => {
    if (notificationsData.length) {
      const groupNotifications = notificationsData.filter(
          (n) =>
              n.group === group._id && Number(n.user) === Number(authStore.userData.id)
      );

      if (selectedGroup.value && selectedGroup.value._id === group._id) {
        group.notifications = 0;
      } else {
        group.notifications = groupNotifications.length;
      }
    }

    return group;
  });
};

const refreshGroups = () => {
  getGroups();
};

const getGroupUsersIds = (group) => {
  const {users} = group;

  return users && users.length > 0 && typeof users[0] === "object"
      ? users.map((u) => u.user_id)
      : users;
};

const getGroups = (group) => {
  const gUsersIds = group ? getGroupUsersIds(group) : [];

  socketStore
      .ensureToken()
      .then((hasToken) => {
        if (!hasToken || !socketStore.chatToken) return;

        return axios.get(`${socketStore.socketHost}/groups/${window.store.subdomain}/${authStore.userData.id}`, {
          withCredentials: false,
          headers: { Authorization: `Bearer ${socketStore.chatToken}` },
          // In a session the server answers with that room's chat and nothing else.
          ...(props.inSession && sessionId.value ? { params: { session: sessionId.value } } : {}),
        });
      })
      .then((response) => {
        if (!response) return;

        if (response.data) {
          groups.value = response.data;
          // Sort the groups by lastMessage.created_at in descending order
          groups.value = groups.value.sort((a, b) => {
            const dateA = a.lastMessage.created_at
                ? new Date(a.lastMessage.created_at)
                : new Date(0);
            const dateB = b.lastMessage.created_at
                ? new Date(b.lastMessage.created_at)
                : new Date(0);
            return dateB - dateA;
          });

          if (selectedGroup.value) {
            if (groups.value.length > 0) {
              const isGroupAvailable = groups.value.find((g) => {
                const idMatch = g._id === selectedGroup.value._id;
                if (!idMatch) return false;
                if (g.isSessionGroup && String(g.sessionId) !== String(sessionId.value)) {
                  return false;
                }
                return true;
              });

              if (!isGroupAvailable) {
                selectArea(areas.value[0]);
              } else {
                const updatedGroup = {...selectedGroup.value};

                if (group) {
                  updatedGroup.users = updatedGroup.users.filter((u) =>
                      gUsersIds.includes(u.user_id)
                  );
                }

                selectGroup(isGroupAvailable);
              }
            } else {
              selectArea(areas.value[0]);
            }
          } else {
            if (paramGroup.value) {
              const startWithGroup = groups.value.find(
                  (g) => g._id === paramGroup.value
              );
              selectGroup(startWithGroup);
            }
          }
        }
      })
      .catch((error) => {
        console.error('Error retrieving groups:', error);
      });
};

const selectArea = (selectedAreaData) => {
  selectedGroup.value = null;
  selectedArea.value = selectedAreaData;

  areas.value = areas.value.map((a) => {
    a.active = a.id === selectedAreaData.id;
    return a;
  });

  users.value = selectedAreaData.users.filter(
      (u) => u.user_id !== Number(authStore.userData.id)
  );

  if (document.getElementById("boxChat")?.offsetWidth > 890) {
    setFullTemplate();
  }
};

const selectGroup = async (group) => {
  // Last line of defence for the entry points that don't read groupList (a
  // ?group= deep link, a queued notification bubble). The websocket refuses to
  // return the messages anyway, which would leave an empty chat open.
  if (isHiddenPeerChat(group)) return;

  view.value = "messages";

  if (document.getElementById("boxChat")?.offsetWidth > 890 && !jitsi.start) {
    template.value = "full";
  }

  if (jitsi.api && selectedGroup.value._id !== group._id) {
    jitsi.warning = true;
    jitsi.group = group;
  } else {
    areas.value = areas.value.map((a) => {
      a.active = false;
      return a;
    });

    if (group) {
      selectedGroup.value = group;

      groups.value = groups.value.map((g) => {
        if (g._id === group._id) g.notifications = 0;
        return g;
      });

      socketStore.socket.emit("user_group_select", {
        domain: window.store.subdomain,
        group: group._id,
        user_id: authStore.userData.id,
        message: null,
      });
    }
  }

  try {
    await saveInteractionData(authStore.userData, 'omnichat-select-group');
  } catch (error) {
    console.error(error);
  }
};


const closeGroup = (refresh) => {
  const {_id: groupId, videoUsers} = selectedGroup.value;

  socketStore.socket.emit("user_group_update", {
    domain: window.store.subdomain,
    groupId: groupId,
    action: "group_video",
    videoUsers: videoUsers.filter((id) => id !== authStore.userData.id),
  });

  if (refresh && areas.value.length > 0) {
    selectArea(areas.value[0]);
  } else {
    refreshGroups();
  }
};

const updateGroupVideo = (videoId, group) => {
  videoUrl.value = videoId;
  const {_id: groupId} = selectedGroup.value;

  emitGroupUpdate({
    domain: window.store.subdomain,
    groupId: groupId,
    action: "group_video",
    videoUrl: videoUrl.value,
    videoUsers: selectedGroup.value.videoUsers
        ? [...selectedGroup.value.videoUsers, authStore.userData.id]
        : [authStore.userData.id],
  });

  const message =
      group.videoUsers && group.videoUsers.length > 0
          ? "has joined the video call"
          : "has started a video call";

  if (videoId) {
    socketStore.socket.emit("user_group_message", {
      domain: window.store.subdomain,
      group: groupId,
      user_id: authStore.userData.id,
      message,
    });
  }
};

const leaveVideo = (hangup = true) => {
  if (groupsComponentRef.value?.videoStart) {
    groupsComponentRef.value.handleLeaveVideo();
  }

  jitsiLoaded.value = false;
  jitsi.isFull = false;
  isChatHidden.value = false;
  scrollToBottom();
  const boxchat = document.getElementById("boxChat");
  const eBox = boxchat?.querySelector(".e-box");
  if (jitsi.start && eBox) {
    eBox.classList.remove("e-box--chat-jitsi");
  }
  if (jitsi.api && hangup) {
    jitsi.api.executeCommand("hangup");
  }

  const user = authStore.userData;

  if (selectedGroup.value) {
    selectedGroup.value.messages.push({
      user: {
        fullName: user.fullName,
        user_id: user.id,
      },
      message: "you have left the video call",
      created_at: new Date().toISOString()
    });

    selectedGroup.value.lastMessage = {
      full_name: user.fullName,
      message: "you have left the video call",
      created_at: new Date().toISOString()
    };
  }

  setTimeout(() => {
    jitsi.start = false;
  }, 0);
};

const startVideo = (group) => {
  const videoId = group.videoUrl
      ? group.videoUrl
      : crypto.randomUUID();
  const user = authStore.userData;
  template.value = "default";
  jitsi.start = true;
  if (selectedGroup.value) {
    const message = group.videoUsers && group.videoUsers.length > 0
        ? "you have joined the video call"
        : "you have started a video call";
    selectedGroup.value.messages.push({
      user: {
        fullName: user.fullName,
        user_id: user.id,
      },
      message,
      created_at: new Date().toISOString()
    });

    selectedGroup.value.lastMessage = {
      full_name: user.fullName,
      message,
      created_at: new Date().toISOString()
    };

    scrollToBottom();
  }

  updateGroupVideo(videoId, group);

};

const emitGroupUpdate = (args) => {
  socketStore.socket.emit("user_group_update", args);
};

const handleClose = () => {
  // window.parent.closeChatSession();
  // socketStore.socket.emit("chat_close", {
  //   user_id: authStore.userData.id,
  //   current_subdomain: window.store.subdomain,
  // });
};

onMounted(async () => {
  if (!authStore.userData?.id) {
    const user = authStore.userData;
    if (user && user.id) {
      // window.userId = user.id;
      // window.subdomainUserId = user.subdomain_user_id;
    }
  }
  const uri = window.location.search.substring(1);
  const params = new URLSearchParams(uri);
  paramGroup.value = params.get("group");

  // show only notification
  const showOnlyNotificationParam = params.get("notification");

  if (showOnlyNotificationParam) {
    if (window.parentNotifications) {
      window.parentNotifications.style.height = `0px`;
    }

    showOnlyNotification.value = true;
  }

  // user invisible
  socketListeners.on("user_visibility", ({user_id, invisible, domain}) => {
    if (domain === window.store.subdomain) {
      areas.value = areas.value.map((a) => {
        a.users = a.users.map((u) => {
          if (u.user_id === Number(user_id)) u.invisible = invisible;
          return u;
        });
        return a;
      });

      // Update sessionStore participants with invisible property
      sessionStore.setParticipantInvisible(String(user_id), invisible);
    }
  });

  socketStore.socket.emit("new_chat_open", {
    domain: window.store.subdomain,
    userId: authStore.userData.id,
    subdomainUserId: authStore.userData.subdomain_user_id,
    attendee: authStore.userRole === "attendee",
  });

  /**
   * A setting changed while this room is running.
   *
   * Everything on the session-settings card is otherwise read once, at join,
   * so turning chat off mid-session did nothing until everybody reloaded.
   * The whole settings object travels, so this applies an answer rather than
   * reconciling a diff — a dropped message leaves the room one update behind,
   * not in a state nobody intended.
   *
   * Scoped to this session as well as this tenant: two rooms can be open in
   * one browser, and a change to one must not reach into the other.
   */
  socketListeners.on("session_settings:changed", (payload) => {
    if (payload?.domain !== window.store.subdomain) return;
    if (Number(payload?.agenda_event_id) !== Number(agendaEventIdForChat.value)) return;

    applyRoomFeatures(payload.settings ?? {});

    if (typeof payload.in_session_chat_enabled === 'boolean') {
      sessionChatEnabledFromApi.value = payload.in_session_chat_enabled;
    }
  });

  socketListeners.on("area_refresh", ({areas: newAreas, domain}) => {
    if (window.store.subdomain === domain && newAreas.length > 0) {
      if (newAreas.length > 0) areas.value = newAreas;

      const activeArea = areas.value.find((a) => a.active);
      if (activeArea) {
        if (typeof activeArea.peerMessagingEnabled === "boolean") {
          peerMessagingEnabled.value = activeArea.peerMessagingEnabled;
        }
        const allUsersData = activeArea.users;
        allUsers.value = allUsersData;
        users.value = allUsersData.filter(
            (u) => u.user_id !== Number(authStore.userData.id)
        );

        const currentUser = allUsers.value.find(
            (u) => u.user_id === Number(authStore.userData.id)
        );

        if (currentUser) {
          newUser.value = {
            ...currentUser,
            display_name: currentUser.fullName,
            user_email: currentUser.email,
            id: currentUser.user_id,
          };
          newUserReady.value = true;
        }

        groups.value = groups.value.map((g) => {
          g.users = g.users.map((u) => {
            const updatedUser = allUsersData.find(
                (au) => au.user_id === u.user_id
            );
            if (updatedUser) u = {...u, ...updatedUser};
            return u;
          });
          return g;
        });
      }
    }
  });

  socketListeners.on("video_invite", (payload) => {
    const {users: invitedUsers, user_id, videoUrl: videoUrlData, domain} = payload;

    if (
        domain === window.store.subdomain &&
        invitedUsers.includes(authStore.userData.id) &&
        user_id !== authStore.userData.id
    ) {
      notifications.value = [
        {
          type: "message",
          message: videoUrlData,
        },
      ];
    }
  });

  socketListeners.on("groups_refresh", (payload) => {
    const {domain, refresh, group, action, addedUser, users: groupUsers} = payload;

    if (domain === window.store.subdomain) {
      if (action === "group_delete") {
        // selectedGroup may be null (no chat open, or it was just deselected).
        // Guard the deref — otherwise this throws before the filter below runs,
        // leaving the deleted chat in the list (messages cleared but not removed).
        if (selectedGroup.value && payload._id === selectedGroup.value._id) {
          view.value = "groups";
          selectedGroup.value = null;
        }
        groups.value = groups.value.filter((g) => g._id !== payload._id);
      } else if (action === "group_video" && group) {
        if (selectedGroup.value && group._id === selectedGroup.value._id) {
          selectedGroup.value.videoUsers = group.videoUsers;
          selectedGroup.value.videoUrl = group.videoUrl;
          groups.value = groups.value.map((g) => {
            if (g._id === group._id) {
              return {
                ...g,
                videoUsers: group.videoUsers,
                videoUrl: group.videoUrl,
              };
            } else {
              return g;
            }
          });
        }
      } else if (action === "group_join" && group && addedUser) {
        groups.value = groups.value.map((g) => {
          if (g._id === group._id) {
            return {
              ...g,
              users: [...g.users, addedUser],
              pendingUsers: group.pendingUsers,
            };
          } else {
            return g;
          }
        });
        // find the group in the groups array and if it doesn't exist, add it as the first one
        if (!groups.value.find((g) => g._id === group._id) && addedUser.user_id === authStore.userData.id) {
          const users = group.users.map((groupUser) => {
            const user = allUsers.value.find((user) => user.user_id === groupUser);
            return user;
          });
          const pendingUsers = group.pendingUsers.map((groupUser) => {
            const user = allUsers.value.find((user) => user.user_id === groupUser);
            return user;
          });
          const updatedGroup = {
            ...group,
            users,
            pendingUsers,
            notifications: 0,
            lastMessage: {
              user_id: addedUser.user_id,
              message: "joined the group chat",
              full_name: addedUser.fullName,
              group: payload,
              created_at: new Date(),
            }
          };
          groups.value.unshift(updatedGroup);
        }
      } else if (
          group &&
          groups.value.length > 0 &&
          groups.value.find((g) => g._id === group._id) &&
          action !== "group_leave"
      ) {
        if (action !== "group_invite" && action !== "group_create_join_session") {
          getGroups(group);
        }
      } else if (action === "group_create_join_session" && group) {
        // Handle new session group creation
        const gUsersIds = getGroupUsersIds(group);
        if (gUsersIds && gUsersIds.includes(Number(authStore.userData.id))) {
          getGroups(group);
        }
      }

      if (refresh && action !== "group_invite") {
        getGroups(group);
      }
      if (action !== "group_video" && action !== "group_join" && action !== "group_invite" && action !== "group_delete") {
        const targetGroup = group || payload;
        const gUsersIds = getGroupUsersIds(targetGroup);
        if (
            gUsersIds &&
            gUsersIds.includes(Number(authStore.userData.id)) &&
            action !== "group_leave" &&
            action !== "group_create_join_session"
        ) {
          getGroups(targetGroup);
        }
      }

      if (
          action === "group_leave" &&
          groupUsers.includes(authStore.userData.id)
      ) {
        handleChangeView("groups");
        refreshGroups();
      }

      getGroupsNotifications();
    }
  });

  socketListeners.on("group_messages_refresh", (payload) => {
    const {domain, group, messages, lastMessage} = payload;

    if (domain === window.store.subdomain) {
      if (lastMessage) {
        groups.value = groups.value.map((group) => {
          if (group.groupId === lastMessage.group) {
            return {
              ...group,
              lastMessage,
            };
          } else {
            return group;
          }
        });
      }

      // Somebody else wrote in the session's chat: the phone's notice says
      // who and what, with Reply, while Chat is closed (Session.vue decides).
      if (props.inSession) {
        const notice = chatNoticeFrom(payload, {
          chatId: sessionChat.value?.groupId ?? sessionChat.value?._id ?? null,
          myId: authStore.userData?.id ?? null,
        });
        if (notice) sessionStore.setChatNotice({ ...notice, at: Date.now() });
      }

      if (selectedGroup.value && selectedGroup.value._id === group) {
        // Replace the object (rather than mutate `.messages`) so GroupsComponent's
        // `messages` computed reliably re-runs — a freshly opened chat has no
        // `messages` key yet, and adding it via mutation can be missed on first
        // paint, leaving history blank until the next message.
        selectedGroup.value = { ...selectedGroup.value, messages: messages || [] };

        socketStore.socket.emit("notifications_update", {
          domain: window.store.subdomain,
          group: selectedGroup.value._id,
          user_id: authStore.userData.id,
        });

        scrollToBottom();
      }

      // Sort the groups by lastMessage.created_at in descending order
      groups.value = groups.value.sort((a, b) => {
        const dateA = a.lastMessage.created_at
            ? new Date(a.lastMessage.created_at)
            : new Date(0);
        const dateB = b.lastMessage.created_at
            ? new Date(b.lastMessage.created_at)
            : new Date(0);
        return dateB - dateA;
      });

      getGroupsNotifications();
    }
  });

  // get website areas
  const hasToken = await socketStore.ensureToken();

  if (hasToken && socketStore.chatToken) {
    axios
        .get(`${socketStore.socketHost}/areas/${window.store.subdomain}`, {
          withCredentials: false,
          headers: { Authorization: `Bearer ${socketStore.chatToken}` },
        })
        .then((response) => {
        if (response.data) {
          const areasData = response.data;
          allUsers.value =
              areasData && areasData.length > 0 && areasData[0].users ? areasData[0].users : [];

          areas.value = areasData;

          const activeArea = areas.value.find((a) => a.active);
          if (activeArea) {
            selectedArea.value = activeArea;
            if (typeof activeArea.peerMessagingEnabled === "boolean") {
              peerMessagingEnabled.value = activeArea.peerMessagingEnabled;
            }
            users.value = activeArea.users.filter(
                (u) => u.user_id !== Number(authStore.userData.id)
            );
          }

          const currentUser = allUsers.value.find(
              (u) => u.user_id === Number(authStore.userData.id)
          );

          if (currentUser) {
            user.value = {
              ...currentUser,
              display_name: currentUser.fullName,
              user_email: currentUser.email,
              id: currentUser.user_id,
            };
          }

          refreshGroups();
        }
        })
        .catch((error) => {
          console.error('Error retrieving areas:', error);
        });
  }

  getGroupsNotifications();

  if (document.getElementById("boxChat")) {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const {width} = entry.contentRect;
        if (width > 890) {
          setFullTemplate();
        } else {
          if (!view.value) view.value = "attendees";
          template.value = "default";
        }
      }
    });

    resizeObserver.observe(document.getElementById("boxChat"));
  }

  window.leaveVideo = leaveVideo;

  // The settings lookup runs from the agendaEventIdForChat watcher, at once.

  isLoading.value = false;
});

const emit = defineEmits(['notifications-updated', 'minimize']);

watch([notificationsCount, qnaNotifications, pollsNotifications], () => {
  emit('notifications-updated', {
    chats: notificationsCount.value,
    qna: qnaNotifications.value,
    polls: pollsNotifications.value
  });
}, {immediate: true});

// Auto-switch to People tab when participant moves into a breakout room
watch(() => sessionStore.isInBreakout, (inBreakout) => {
  // Clear selected group when context switches to prevent cross-scope messaging
  selectedGroup.value = null;
  if (inBreakout && props.inSession) {
    handleChangeView('attendees');
  } else if (!inBreakout && props.inSession) {
    // Back in the main room, attendees land on People, where the panel starts.
    // Admins/Speakers stay where they are (likely managing breakouts) to see
    // the ended state.
    if (props.userRole !== 'admin' && props.userRole !== 'speaker') {
      handleChangeView('attendees');
    }
  }
});

// expose methods to the parent component
// A rail badge never grows past two digits and a plus.
const badge = (n) => (n > 99 ? '99+' : String(n));

defineExpose({
  sessionChatsVisible,
  isOfficeHoursSession,
  setFullTemplate,
  setDefaultTemplate,
  handleChangeView,
  view,
  selectedGroup,
  selectGroup,
  notificationsCount,
  qnaNotifications,
  pollsNotifications
});
</script>

<template>
  <div :class="{
      wrap: true,
      'wrap--full': event || showOnlyNotification,
      'wrap--box': hideHeader,
    }">
    <notification-toast :args="notificationToast" @close="notificationToast.visible = false"/>
    <div id="notifications" v-if="visibleNotifications.length" class="no-scrollbar">
      <div class="notifications__wrapper">
        <notification-bubble
            v-for="notification in visibleNotifications"
            :key="notification._id"
            :notifications="visibleNotifications"
            :notification="notification"
            @refresh-groups="refreshGroups"
            @enter-group="selectGroup"
        />
      </div>
    </div>

    <loading v-if="isLoading" variant="rows" :rows="5" />

    <template v-else>
      <div :class="`omnichat template-${template}`">
        <div class="omnichat__block__content">
          <div class="omnichat__header" v-if="!hideHeader">
            <div class="omnichat__header-logo">
              <icon-logo/>
            </div>
            <div class="omnichat__header-title">OmniChat</div>
            <button class="btn--cls" @click="handleClose">
              <icon-close color="#000"/>
            </button>
          </div>

          <div class="omnichat__content">
            <!-- Rooms: a section of the panel now, over the content area only —
                 the panel's header names it and the rail stays below. -->
            <div v-if="inSession && sessionStore.breakoutsEnabled" class="breakouts-container" v-show="view === 'breakouts'" :class="{ hidden: view !== 'breakouts' }">
              <BreakoutRoomsWidget
                  :session-id="sessionId"
                  :domain="store.subdomain"
                  :is-active="view === 'breakouts'"
                  @back="handleChangeView('attendees')"
                  @close="$emit('minimize')"
              />
            </div>
            <UsersComponent
                v-if="newUserReady"
:class="peopleListHidden ? 'hidden' : undefined" :template="template" :view="view"
                :area="selectedArea"
                :selected-group="selectedGroup"
                :groups="groupList"
                :all-users="allUsers"
                :areas="areas"
                :user="user"
                :newUser="newUser"
                :users="userList"
                :search-data="searchData"
                :user-show="(userData) => (userView = userData)"
                :in-session="inSession"
                :session="session"
                :session-id="sessionId"
                :peer-messaging-enabled="peerMessagingEnabled"
                :in-session-chat-enabled="inSessionChatEnabled"
                :session-chats-visible="sessionChatsVisible"
                :current-user-role="currentUserRole"
                @enterGroup="selectGroup"
                @startVideo="startVideo"
                @changeView="handleChangeView"
                @notify="handleNotify"
                @hide-chat="hideChat"
                @refresh-groups="refreshGroups"
            />

            <!-- The session's chat before it exists (it starts once a second
                 person is in the room): the prototype's empty state. -->
            <div v-if="inSession && view === 'groups' && !sessionChat" class="ses-chat-empty" data-testid="session-chat-empty">
              <span class="ses-chat-empty__g" aria-hidden="true"><SessionPanelIcon name="chat" :size="22" /></span>
              <b>No messages yet</b>
              <p v-if="sessionStore.phoneRoom">Anything you type here is seen by everyone in the session.</p>
              <p v-else>Anything typed here is seen by everybody in the session, and goes when the session does.</p>
            </div>

            <div v-if="sectionAvailable('qanda')" class="qna-container" v-show="view === 'qanda'" :class="{ hidden: view !== 'qanda' }">
              <QnAWidget
                  :session-id="sessionId"
                  :user-id="participantIdentity"
                  :user-name="userName"
                  :user-role="effectiveUserRole"
                  :domain="store.subdomain"
                  :is-active="view === 'qanda'"
                  @update-notifications="(n) => qnaNotifications = n"
              />
            </div>

            <div v-if="sectionAvailable('polls')" class="polls-container" v-show="view === 'polls'" :class="{ hidden: view !== 'polls' }">
              <PollsWidget
                  :session-id="sessionId"
                  :user-id="participantIdentity"
                  :user-name="userName"
                  :user-role="effectiveUserRole"
                  :domain="store.subdomain"
                  :is-active="view === 'polls'"
                  @update-notifications="(n) => pollsNotifications = n"
              />
            </div>

            <!-- Mounted once, like Q&A and Polls: opening Files again shows the
                 list as it was, kept current by resources_refresh, rather than
                 loading it from scratch on every visit. -->
            <div v-if="sectionAvailable('files')" class="files-container" v-show="view === 'files'" :class="{ hidden: view !== 'files' }">
              <EventResources
                  :event="session" :type="isModerator ? 'admin-settings' : 'attendee-popup'"
                  :phone="sessionStore.phoneRoom"
                  @files-shared="sessionStore.noteFilesShared"
                  @downloaded="(name) => sessionStore.setPhoneConfirm({ name: 'Downloaded', text: name, icon: 'download', at: Date.now() })"
              />
            </div>

            <div
                class="chat"
                :class="selectedGroup && view === 'messages' ? undefined : 'hidden'"
                @transitionend="handleChatsTransitionEnd"
            >
              <GroupsComponent
                  v-if="selectedGroup"
                  ref="groupsComponentRef"
                  :template="template"
                  :jitsi="jitsi"
                  :all-users="allUsers"
                  :user="user"
                  :view="view"
                  :video-url="videoUrl"
                  :group="selectedGroup"
                  :all-notifications="notificationsCount"
                  :close-group="closeGroup"
                  :user-show="(userData) => (userView = userData)"
                  :is-chat-hidden="isChatHidden"
                  :jitsi-loaded="jitsiLoaded"
                  :in-session="inSession"
                  :session="session"
                  :in-session-chat-enabled="inSessionChatEnabled"
                  :session-chats-visible="sessionChatsVisible"
                  :leave-video="leaveVideo"
                  @selectGroup="selectGroup"
                  @hide-chat="handleHideChat"
                  @startVideo="startVideo"
                  @joinVideo="startVideo"
                  @view-update="(newView) => (view = newView)"
                  @attendee-left="handleAttendeeLeft"
                  @back="handleBack"
                  @message-sent="onMessageSent"
                  @rename-group="(name) => (selectedGroup.name = name)"
                  @reactions-update="(index, reactions) => (selectedGroup.messages[index].reactions = reactions)"
              />
            </div>

          </div>
          <div v-if="!inSession && view !== 'messages'" class="omnichat__footer">
            <template v-if="!inSession">
              <div
                  :class="view === 'attendees' && 'active'"
                  @click="() => handleChangeView('attendees')"
              >
                <button>
                  <icon-attendees-selected v-if="view === 'attendees'"/>
                  <icon-attendees v-else/>
                  <span>Members</span>
                </button>
              </div>
              <div
                  :class="(view === 'groups' || view === 'messages') && 'active'"
                  @click="handleChangeView('groups')"
              >
                <button style="position: relative">
                  <notifications-count
                      :count="notificationsCount"
                      style="position: absolute; top: 0; left: 50%"
                  />
                  <icon-chats-selected v-if="view === 'groups' || view === 'messages'"/>
                  <icon-chats v-else/>
                  <span>Chats</span>
                </button>
              </div>
            </template>

          </div>

          <!--
            The session panel's rail, as the prototype draws it (its .rmnav):
            labelled icons along the bottom, the open section marked by a
            purple bar on its top edge, unread counts as red badges. Order is
            the prototype's — Chat, Q&A, Polls, Rooms, Files, People — and
            Breakout Rooms is a section of its own (it was opened from People),
            for whoever could open it there.
          -->
          <!-- Office Hours has one section, People, so its rail carries only the
               fold control (prototype cd178, .rmnav.one). -->
          <nav v-if="inSession && isOfficeHoursSession" class="ses-rail ses-rail--one" data-testid="session-rail-one">
            <button type="button" class="ses-rail__fold" title="Hide the panel" aria-label="Hide the panel" @click="emit('minimize')">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9.8 18 6-6-6-6" /></svg>
            </button>
          </nav>
          <nav v-else-if="inSession" class="ses-rail" aria-label="Session panel sections">
            <button
                v-if="sessionChatsVisible"
                id="session-groups-tab"
                type="button"
                :class="{ on: view === 'groups' || view === 'messages' }"
                :aria-current="view === 'groups' || view === 'messages' ? 'page' : undefined"
                @click="handleChangeView('groups')"
            >
              <span class="ses-rail__icon">
                <SessionPanelIcon name="chat" />
                <i v-if="notificationsCount > 0" class="ses-rail__badge">{{ badge(notificationsCount) }}</i>
              </span>
              <span class="ses-rail__label">Chat</span>
            </button>
            <button
                v-if="!sessionStore.isInBreakout && roomFeatures.qa"
                id="session-qanda-tab"
                type="button"
                :class="{ on: view === 'qanda' }"
                :aria-current="view === 'qanda' ? 'page' : undefined"
                @click="handleChangeView('qanda')"
            >
              <span class="ses-rail__icon">
                <SessionPanelIcon name="qa" />
                <i v-if="qnaNotifications > 0" class="ses-rail__badge">{{ badge(qnaNotifications) }}</i>
              </span>
              <span class="ses-rail__label">Q&amp;A</span>
            </button>
            <button
                v-if="!sessionStore.isInBreakout && roomFeatures.polls"
                id="session-polls-tab"
                type="button"
                :class="{ on: view === 'polls' }"
                :aria-current="view === 'polls' ? 'page' : undefined"
                @click="handleChangeView('polls')"
            >
              <span class="ses-rail__icon">
                <SessionPanelIcon name="polls" />
                <i v-if="pollsNotifications > 0" class="ses-rail__badge">{{ badge(pollsNotifications) }}</i>
              </span>
              <span class="ses-rail__label">Polls</span>
            </button>
            <button
                v-if="sessionStore.isAdmin && sessionStore.breakoutsEnabled"
                id="session-breakouts-tab"
                type="button"
                :class="{ on: view === 'breakouts' }"
                :aria-current="view === 'breakouts' ? 'page' : undefined"
                @click="handleChangeView('breakouts')"
            >
              <span class="ses-rail__icon"><SessionPanelIcon name="rooms" /></span>
              <span class="ses-rail__label">Rooms</span>
            </button>
            <button
                v-if="sectionAvailable('files')"
                id="session-files-tab"
                type="button"
                :class="{ on: view === 'files' }"
                :aria-current="view === 'files' ? 'page' : undefined"
                @click="handleChangeView('files')"
            >
              <span class="ses-rail__icon"><SessionPanelIcon name="files" /></span>
              <span class="ses-rail__label">Files</span>
            </button>
            <button
                id="session-attendees-tab"
                type="button"
                :class="{ on: view === 'attendees' }"
                :aria-current="view === 'attendees' ? 'page' : undefined"
                @click="handleChangeView('attendees')"
            >
              <!-- Somebody waiting to come in: a red count, not a dot — "a number
                   says three people are standing outside" (prototype cd154). -->
              <span class="ses-rail__icon">
                <SessionPanelIcon name="people" />
                <!-- A hand up: a dot for the host, so it is seen with the panel
                     closed (prototype cd197 knockdot). Its own corner, clear of
                     the count. -->
                <i
                    v-if="sessionStore.isAdmin && sessionStore.handQueue.length"
                    class="ses-rail__hand"
                    :title="`${sessionStore.handQueue.length} with a hand up`"
                    data-testid="people-hand-dot"
                />
                <i v-if="sessionStore.isAdmin && sessionStore.waitingAttendees.length" class="ses-rail__badge" data-testid="people-waiting-badge">{{ badge(sessionStore.waitingAttendees.length) }}</i>
                <i v-else-if="peopleInSession" class="ses-rail__badge ses-rail__badge--count" data-testid="people-count-badge">{{ badge(peopleInSession) }}</i>
              </span>
              <span class="ses-rail__label">People</span>
            </button>
          </nav>
        </div>
      </div>

      <modal
          v-if="userView"
          close-header-button
          :width="view === 'messages' ? '296px' : '80%'"
          @close="() => (userView = null)"
      >
        <template #header>
          <div class="user-info">
            <div class="user-info__avatar">
              <img
                  v-if="userView.avatar"
                  :src="userView.avatar"
                  :alt="userView.fullName"
                  :title="userView.fullName"
              />

              <svg
                  v-else
                  width="84"
                  height="84"
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                    x="0.25"
                    y="0.25"
                    width="39.5"
                    height="39.5"
                    rx="19.75"
                    stroke="#CEC3CE"
                    stroke-width="0.5"
                />
                <path
                    d="M29.6503 27.8756C28.2225 25.4072 26.0222 23.6372 23.4544 22.7981C24.7246 22.042 25.7114 20.8898 26.2634 19.5185C26.8153 18.1473 26.9019 16.6327 26.5098 15.2075C26.1177 13.7823 25.2685 12.5252 24.0928 11.6292C22.9171 10.7333 21.4798 10.248 20.0016 10.248C18.5234 10.248 17.0861 10.7333 15.9104 11.6292C14.7346 12.5252 13.8855 13.7823 13.4934 15.2075C13.1013 16.6327 13.1879 18.1473 13.7398 19.5185C14.2918 20.8898 15.2786 22.042 16.5488 22.7981C13.981 23.6362 11.7807 25.4062 10.3528 27.8756C10.3005 27.961 10.2658 28.056 10.2507 28.155C10.2357 28.254 10.2406 28.355 10.2652 28.4521C10.2898 28.5492 10.3337 28.6404 10.3941 28.7202C10.4545 28.8001 10.5303 28.867 10.6171 28.9171C10.7038 28.9672 10.7997 28.9995 10.899 29.0119C10.9984 29.0243 11.0993 29.0167 11.1957 28.9896C11.2921 28.9624 11.3821 28.9162 11.4603 28.8537C11.5386 28.7912 11.6035 28.7136 11.6513 28.6256C13.4175 25.5731 16.5394 23.7506 20.0016 23.7506C23.4638 23.7506 26.5857 25.5731 28.3519 28.6256C28.3997 28.7136 28.4646 28.7912 28.5429 28.8537C28.6211 28.9162 28.7111 28.9624 28.8075 28.9896C28.9039 29.0167 29.0048 29.0243 29.1042 29.0119C29.2035 28.9995 29.2994 28.9672 29.3861 28.9171C29.4729 28.867 29.5487 28.8001 29.6091 28.7202C29.6695 28.6404 29.7134 28.5492 29.738 28.4521C29.7626 28.355 29.7675 28.254 29.7525 28.155C29.7374 28.056 29.7027 27.961 29.6503 27.8756ZM14.7516 17.0006C14.7516 15.9622 15.0595 14.9472 15.6364 14.0839C16.2133 13.2205 17.0332 12.5476 17.9925 12.1502C18.9518 11.7529 20.0074 11.6489 21.0258 11.8515C22.0442 12.054 22.9797 12.5541 23.7139 13.2883C24.4481 14.0225 24.9481 14.958 25.1507 15.9764C25.3533 16.9948 25.2493 18.0504 24.852 19.0097C24.4546 19.969 23.7817 20.7889 22.9183 21.3658C22.055 21.9427 21.0399 22.2506 20.0016 22.2506C18.6097 22.2491 17.2752 21.6955 16.2909 20.7113C15.3067 19.727 14.7531 18.3925 14.7516 17.0006Z"
                    fill="#4B444D"
                />
              </svg>
            </div>
            <div class="user-info__content">
              <div class="user-info__title">{{ userView.fullName }}</div>
            </div>
          </div>
        </template>
      </modal>

      <modal
          title="Warning"
          on-close-text="CANCEL"
          @close="
          () => {
            jitsi.group = null;
            jitsi.warning = false;
          }
        "
          on-success-text="OK"
          @success="
          () => {
            leaveVideo();
          }
        " v-if="jitsi.warning">
        <p>
          If you switch to another OmniChat group, this video call session will
          close. You can always rejoin the call.
        </p>
      </modal>
    </template>
  </div>
</template>

<style scoped lang="scss">
.wrap {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.omnichat {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.notifications__wrapper {
  display: flex;
  flex-direction: column;
  row-gap: 10px;
}

.omnichat__block__content {
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
}

.omnichat__content {
  flex: 1;
  height: 0; /* Force flex to constrain height */
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  position: relative;
}

.omnichat__footer {
  flex-shrink: 0;
  position: relative;
  z-index: 10;
}

.qna-container, .polls-container, .files-container, .chat {
  flex: 1;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.breakouts-container {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: #fff;
  display: flex;
  flex-direction: column;
}

/* The session panel's rail (prototype .rmnav). */
.ses-rail {
  flex: 0 0 auto;
  display: flex;
  align-items: stretch;
  border-top: 1px solid var(--xl-color-border, #EDE7F0);
  background: #fff;
  font-family: var(--xl-font-family);
}

.ses-rail button {
  flex: 1;
  min-width: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 8px 2px 7px;
  border: 0;
  background: transparent;
  color: var(--xl-color-outline, #867D8D);
  cursor: pointer;
  transition: color 0.15s;
}

.ses-rail button:hover { color: var(--xl-color-on-surface-variant, #514A57); }
.ses-rail button:focus-visible { outline: 2px solid var(--xl-color-primary, #5E2F78); outline-offset: -2px; border-radius: 8px; }
.ses-rail button.on { color: var(--xl-color-primary, #5E2F78); }

/* The open section: a 2px bar riding on the rail's top edge. */
.ses-rail button.on::before {
  content: "";
  position: absolute;
  top: -1px;
  left: 22%;
  right: 22%;
  height: 2px;
  border-radius: 2px;
  background: var(--xl-color-primary, #5E2F78);
}

.ses-rail__icon { position: relative; display: grid; place-items: center; }

/* One section, so the rail carries only the fold control (prototype .rmnav.one, .navfold). */
.ses-rail--one { justify-content: flex-end; }
.ses-rail .ses-rail__fold {
  flex: 0 0 42px;
  justify-content: center;
  padding: 8px 2px 7px;
  border-radius: 0;
}
.ses-rail .ses-rail__fold:hover { background: var(--xl-color-border-subtle, #F4EFF5); color: var(--xl-color-on-surface, #1C1720); }
.ses-rail__label { font-size: 10px; font-weight: 600; letter-spacing: -0.01em; white-space: nowrap; }

/* The session's own chat before anybody has written (prototype .rmempty). */
.ses-chat-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 30px 24px;
  text-align: center;
  font-family: var(--xl-font-family);
}

.ses-chat-empty__g {
  width: 46px;
  height: 46px;
  margin-bottom: 8px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: var(--xl-color-border-subtle, #F6F2F8);
  color: var(--xl-color-outline, #867D8D);
}

.ses-chat-empty b { font-size: 14.5px; font-weight: 600; color: var(--xl-color-on-surface, #1C1720); }
.ses-chat-empty p { margin: 0 0 4px; max-width: 230px; font-size: 12.5px; line-height: 1.55; color: var(--xl-color-outline, #867D8D); }

/* A headcount (prototype .rmnav .ri i): grey, where a count of something
   waiting on you is red. */
.ses-rail__badge--count.ses-rail__badge--count { background: var(--xl-color-border, #EDE7F0); color: var(--xl-color-on-surface-variant, #514A57); }

/* A hand up (prototype .knockdot): an amber dot, on the icon's other corner. */
.ses-rail__hand {
  position: absolute;
  top: -3px;
  left: -7px;
  width: 8px;
  height: 8px;
  border: 1.5px solid #fff;
  border-radius: 50%;
  background: var(--xl-color-warning, #8A5200);
}

/* An unread count (prototype .rmnav .ri i.r). */
.ses-rail__badge {
  position: absolute;
  top: -5px;
  right: -9px;
  min-width: 15px;
  height: 15px;
  padding: 0 3px;
  border-radius: 99px;
  display: grid;
  place-items: center;
  background: var(--xl-color-error, #A32020);
  color: #fff;
  font-size: 9.5px;
  font-weight: 700;
  font-style: normal;
}

@media (prefers-reduced-motion: reduce) {
  .ses-rail button { transition: none; }
}
</style>

<style>
/* Global style to allow absolute positioning inside the chat modal */
.chat-modal__container {
  position: relative !important;
  overflow: hidden !important; /* Ensure content stays within rounded corners if any */
}
</style>
